import React, {useContext} from 'react'
import {INavNotification} from '@api/user/account/notification/getNotification'
import WizImage from '@component/images/WizImage'
import ic_official_logo from '@image/lot_circle_logo.png'
import TagText from '@component/Text/TagText'
import {passUnixTime} from '@util/time'
import {useTranslation} from '@lib/intl/useTranslations'
import useFormatDate from '@hook/useFormatDate'
import {apiReadNotification} from '@api/user/account/notification/apiReadNotification'
import {useQueryClient} from '@tanstack/react-query'
import {QueryCacheKeys} from '@hook/query/queryKeys'
import {useLoginStore} from '@store/LoginStore'
import {NotificationPopoverContext} from '@component/header/GlobalNavigation/NotificationDropdown/NotificationDropdown'
import useQueryGetNotificationCount from '@hook/query/account/notification/useQueryGetNotificationCount'
import {createLog} from '@util/logs'

interface IProps extends INavNotification {
    thumbnail?: string
    text?: string
    onClick?: () => void
    renderIcon?: React.ReactElement
}

const NotificationItemLayout: React.FC<IProps> = ({
    thumbnail,
    is_read,
    type_icon_url,
    text,
    created_at,
    _id,
    onClick,
    renderIcon,
    type,
}) => {
    const t = useTranslation()
    const {displayDate} = useFormatDate()
    const queryClient = useQueryClient()
    const id = useLoginStore(state => state.id)
    const {setIsOpen} = useContext(NotificationPopoverContext)
    const {refetch: refetchNotificationCount} = useQueryGetNotificationCount(false)
    const dateText = () => {
        const passTimeMinute = Math.floor(passUnixTime(created_at) / (1000 * 60))
        const checkPassTime = Math.floor(passTimeMinute / 60)

        if (checkPassTime < 1) {
            return t('globalNotification.passTime.min', {pass_time: passTimeMinute})
        } else if (checkPassTime >= 1 && checkPassTime < 24) {
            return t('globalNotification.passTime.hour', {pass_time: Math.floor(checkPassTime)})
        } else {
            return displayDate(created_at, t('dateFormat.date'))
        }
    }

    const onClickContainer = async () => {
        onClick && onClick()
        createLog('event', `notification_click_item_${type}`, {notification_id: _id})

        if (!is_read) {
            await apiReadNotification(_id)
        }

        await queryClient.invalidateQueries({queryKey: QueryCacheKeys.notification.getNotificationsList(id)})
        await refetchNotificationCount()
        setIsOpen(false)
    }

    return (
        <div
            className={`py-[15px] w-full px-[20px] flex items-center cursor-pointer hover:bg-primary_tint02 hover:dark:bg-dark_primary_tint02 ${
                is_read === false ? 'bg-primary_tint02 dark:bg-dark_primary_tint02' : 'bg-white dark:bg-transparent'
            }`}
            onClick={onClickContainer}>
            {renderIcon ? (
                <div className={'w-[24px] mr-[20px] flex-none'}>{renderIcon}</div>
            ) : (
                <div
                    className={`dark:invert-[.94] dark:sepia-[.14] dark:saturate-[3.92] dark:hue-rotate-[188deg] dark:brightness-[0.03] dark:contrast-[.94]`}>
                    <WizImage src={type_icon_url} width={24} height={24} containerClassName={'mr-[20px] flex-none'} />
                </div>
            )}

            <div className={'flex-1'}>
                <div className={'flex items-center space-x-[5px]'}>
                    <WizImage
                        src={thumbnail || ic_official_logo}
                        width={15}
                        height={15}
                        containerClassName={'rounded-full overflow-hidden'}
                    />
                    <TagText className={'text-gray03 dark:text-dark_gray03 text-body3'}>{dateText()}</TagText>
                </div>
                <TagText className={'text-body2 text-gray01 dark:text-dark_gray01 break-all'}>{text}</TagText>
            </div>
        </div>
    )
}

export default NotificationItemLayout
